// Small vibration wrapper on top of the Web Vibration API.
//
// Real iOS Safari IGNORES navigator.vibrate — the browser has to be
// installed as a PWA on Android (or run inside a WebView that
// forwards to the OS haptics service) for the buzz to actually
// fire. Nothing to worry about on iOS though: the calls silently
// no-op there, so nothing rings false to the spectator.
//
// Every function accepts a magician-configurable enable flag (kept
// in localStorage so it survives reloads). Off by default? No —
// haptics are a realism boost, so we default them ON.
//
// Public API: hapticTick / hapticTap / hapticSuccess / hapticError.
// Each corresponds to a real iOS interaction feedback tier:
//   • tick    → key press, dot fill (very short 4 ms)
//   • tap     → button press, note open (single short pulse ~10 ms)
//   • success → unlock, save (double soft pulse)
//   • error   → wrong passcode shake (heavy pattern)

import { STORAGE } from "./storage";

// Fallback default when the key was never written — realism-first.
const DEFAULT_ENABLED = true;

/** Read the magician's haptics preference from localStorage. */
export function isHapticsEnabled() {
  const v = localStorage.getItem(STORAGE.HAPTICS_ENABLED);
  if (v === null) return DEFAULT_ENABLED;
  return v === "1";
}

/** Persist the magician's haptics preference. */
export function saveHapticsEnabled(on) {
  localStorage.setItem(STORAGE.HAPTICS_ENABLED, on ? "1" : "0");
}

/** Fire a vibration pattern if the API + user preference allow it. */
function fire(pattern) {
  if (!isHapticsEnabled()) return;
  if (typeof navigator === "undefined") return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* API may throw on unsupported browsers — silent no-op */
  }
}

/** Subtle tick — 4 ms. Used for keypad taps and dot fills. */
export function hapticTick() {
  fire(4);
}

/** Single short pulse — 10 ms. Used for button taps and note opens. */
export function hapticTap() {
  fire(10);
}

/** Success pattern — two short pulses. Used for successful unlock / save. */
export function hapticSuccess() {
  fire([10, 40, 10]);
}

/** Error pattern — three heavier pulses. Used for wrong passcode shake. */
export function hapticError() {
  fire([25, 60, 25, 60, 25]);
}
